import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiCheckCircle } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import type { CapexPlan } from './types'

interface WorkflowBannersProps {
  plan: CapexPlan
  onApprove: () => void
  onRejectClick: () => void
  onResumeDraft: () => void
  isReadOnly: boolean
}

export function WorkflowBanners({
  plan,
  onApprove,
  onRejectClick,
  onResumeDraft,
}: WorkflowBannersProps) {
  const { t } = useTranslation('capex')
  const status = plan.workflow_status ?? 'draft'
  // The backend sets workflow_status back to 'draft' immediately on rejection.
  // We track a local dismissed flag so the user can clear the rejection banner.
  const [rejectionDismissed, setRejectionDismissed] = useState(false)

  if (status === 'submitted') {
    return (
      <div
        className="rounded-md border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-950/30 p-4"
        data-testid="banner-submitted"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
            {t('banners.submitted_title')}
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-green-600 text-green-700 hover:bg-green-50 dark:border-green-500 dark:text-green-400 dark:hover:bg-green-950/30"
              onClick={onApprove}
              data-testid="approve-plan-btn"
            >
              <AppIcon path={mdiCheckCircle} size="sm" className="mr-1.5" />
              {t('banners.approve')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="border-destructive text-destructive hover:bg-destructive/5"
              onClick={onRejectClick}
              data-testid="reject-plan-btn"
            >
              {t('banners.reject')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'approved') {
    return (
      <div
        className="rounded-md border border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950/30 p-4"
        data-testid="banner-approved"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <AppIcon path={mdiCheckCircle} size="sm" className="text-green-600 dark:text-green-400 shrink-0" />
            <p className="text-sm font-medium text-green-800 dark:text-green-300">
              {t('banners.approved_title')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Show rejection banner when:
  // - status is explicitly 'rejected' (future-proofing), OR
  // - status is 'draft' but plan has rejection data (current backend behavior: rejects go
  //   straight to draft with rejection metadata attached)
  const showRejection =
    (status === 'rejected' || (status === 'draft' && plan.rejection != null)) &&
    !rejectionDismissed

  if (showRejection) {
    const rejection = plan.rejection
    return (
      <div
        className="rounded-md border border-destructive/40 bg-destructive/5 p-4"
        data-testid="banner-rejected"
      >
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-destructive">
                {t('banners.rejected_title')}
              </p>
              {rejection?.category && (
                <Badge variant="destructive" className="text-xs">
                  {t(`rejection_category.${rejection.category}`, rejection.category)}
                </Badge>
              )}
            </div>
            {rejection?.note && (
              <p className="text-sm text-muted-foreground">{rejection.note}</p>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setRejectionDismissed(true); onResumeDraft() }}
            data-testid="resume-draft-btn"
          >
            {t('banners.resume_draft')}
          </Button>
        </div>
      </div>
    )
  }

  return null
}
